/**
 * Fait : range plusieurs parties d'écran derrière des ONGLETS — une barre de
 *   choix, et le seul contenu choisi en dessous. Chaque onglet garde la
 *   couleur de la partie qu'il ouvre, pour qu'on retrouve le même repère
 *   qu'avant : on ne change pas de langage visuel, on change d'empilement.
 *
 *   POURQUOI, dans les mots de Charles le 2026-08-27 : « quand tu cliques sur
 *   argent, on a des boîtes qui n'en finissent plus dans le bas de la page. Je
 *   pense que c'est une bonne application des tabulations. » À son fort
 *   grossissement, quatre blocs empilés font une page qu'on parcourt en
 *   aveugle : ce qu'on cherche est toujours trois écrans plus bas.
 *
 *   DE VRAIS ONGLETS, PAS DES BOUTONS QUI SE RESSEMBLENT. Le clavier y navigue
 *   aux flèches, `Début` et `Fin` sautent aux extrémités, et un seul onglet
 *   entre dans le parcours de tabulation — c'est le motif attendu par les
 *   lecteurs d'écran, qui annoncent alors « onglet 2 sur 3, sélectionné ».
 *   Sans cela, on aurait trois boutons à traverser un par un pour atteindre le
 *   contenu.
 *
 *   L'ÉTAT CHOISI NE TIENT PAS À LA COULEUR SEULE. L'onglet ouvert porte en
 *   plus un filet épais de sa couleur sur le côté, une ombre, et son intitulé
 *   dans cette couleur — quatre signaux, parce qu'un seul se perd dès qu'on
 *   voit mal ou qu'on ne distingue pas les teintes.
 *
 * Ne fait pas : il ne retient PAS lui-même l'onglet ouvert — c'est l'écran qui
 *   le garde, et ce n'est pas un détail : l'écran de l'argent se recharge après
 *   chaque enregistrement et démonte tout son contenu. Un choix rangé ici
 *   repartirait au premier onglet à chaque plafond posé. Il ne calcule pas non
 *   plus le contenu des onglets fermés : chaque partie est une fonction, et
 *   seule celle qu'on regarde est appelée.
 */

import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import type { NatureDePartie } from './Partie.tsx';

/** Un onglet : son nom court, sa couleur, et ce qu'il ouvre. */
export interface OngletVu {
  /** Ce qui identifie l'onglet dans le code, et dans les mesures. */
  readonly id: string;

  /** Le nom lisible, écrit sur l'onglet. Court : il doit tenir à 320 points. */
  readonly etiquette: string;

  /** La couleur, reprise de la partie qu'il ouvre. */
  readonly nature: NatureDePartie;

  /** Ce qu'il y a dedans — une FONCTION, pour ne rien calculer d'inutile. */
  readonly rendu: () => ReactNode;
}

export function Onglets({
  onglets,
  choisi,
  surChoix,
  intitule,
}: {
  readonly onglets: readonly OngletVu[];
  readonly choisi: string;
  readonly surChoix: (id: string) => void;

  /** Ce que la barre d'onglets annonce — « Les parties de l'argent ». */
  readonly intitule: string;
}) {
  const marque = useId();
  const boutons = useRef<(HTMLButtonElement | null)[]>([]);

  const actif = onglets.find((onglet) => onglet.id === choisi) ?? onglets[0];

  if (actif === undefined) return null;

  const idDeLOnglet = (id: string) => `${marque}-onglet-${id}`;
  const idDuPanneau = (id: string) => `${marque}-panneau-${id}`;

  /**
   * Les flèches déplacent le choix ET le focus ensemble. Les séparer — choisir
   * sans suivre du regard — laisse la personne devant un contenu qui a changé
   * sans savoir lequel elle vient d'ouvrir.
   */
  function surTouche(evenement: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const dernier = onglets.length - 1;
    let vise: number | null = null;

    if (evenement.key === 'ArrowRight') vise = index === dernier ? 0 : index + 1;
    else if (evenement.key === 'ArrowLeft') vise = index === 0 ? dernier : index - 1;
    else if (evenement.key === 'Home') vise = 0;
    else if (evenement.key === 'End') vise = dernier;

    if (vise === null) return;

    const cible = onglets[vise];
    if (cible === undefined) return;

    evenement.preventDefault();
    surChoix(cible.id);
    boutons.current[vise]?.focus();
  }

  return (
    <div className="empilement" data-role="onglets-et-panneau">
      <div className="onglets" role="tablist" aria-label={intitule} data-role="barre-d-onglets">
        {onglets.map((onglet, index) => {
          const ouvert = onglet.id === actif.id;

          return (
            <button
              key={onglet.id}
              ref={(noeud) => {
                boutons.current[index] = noeud;
              }}
              type="button"
              role="tab"
              id={idDeLOnglet(onglet.id)}
              aria-selected={ouvert}
              aria-controls={idDuPanneau(onglet.id)}
              /* UN SEUL onglet dans le parcours du clavier : à l'intérieur de
                 la barre, ce sont les flèches qui déplacent. */
              tabIndex={ouvert ? 0 : -1}
              data-nature={onglet.nature}
              data-role="onglet"
              data-onglet={onglet.id}
              onClick={() => surChoix(onglet.id)}
              onKeyDown={(evenement) => surTouche(evenement, index)}
            >
              {onglet.etiquette}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={idDuPanneau(actif.id)}
        aria-labelledby={idDeLOnglet(actif.id)}
        data-role="panneau-d-onglet"
        data-onglet={actif.id}
      >
        {actif.rendu()}
      </div>
    </div>
  );
}
